/* eslint-disable max-lines */
import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { faFaceSmilePlus } from '@fortawesome/pro-light-svg-icons/faFaceSmilePlus'
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons/faArrowLeft'
import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight'
import {
  Actions,
  Entities,
  EventNames,
  ScreenNames,
  ComponentSources,
  ArrivedFrom
} from 'src/utils/datahub/enums'
import useSendDataHubConsume from 'src/hook/useSendDatahubConsume/useSendDatahubConsume'
import { useCommunityDetails } from 'src/pages/CommunityDetails/Posts/context/communityDetailsContext'
import { EmojiUtils } from 'src/utils/emoji'
import { Reaction } from 'src/hook/usePosts'
import { addReaction, deleteReaction } from 'src/client/publication'
import usePublicCommunity from 'src/hook/usePublicCommunity'
import { paths } from 'src/router/paths'
import { useMatch } from 'react-router-dom'
import { useOptimizely } from 'src/hook/useOptimizely'
import usePublicCommunityReactionClick from 'src/hook/usePublicCommunityReactionClick/usePublicCommunityReactionClick'
import { useUser } from 'src/hook/useUser'
import Icon from '../Icon'
import PillButton from './pillButton'
import { EmojiPicker } from './emojiPicker'
import { ContainerButtons, Content } from './reactionsBar.styles'
import { EmojiProps, ReactionsBarProps, ReactionsOption } from './reactionsBar.types'
import { ReactionsList } from './reactionsList'

const ReactionsBar = ({
  postId,
  reactionsParam,
  showAnimation,
  className,
  onReactionSuccess,
  onReactionError,
  onReaction
}: ReactionsBarProps) => {
  const { optimizely } = useOptimizely()
  const { community } = useCommunityDetails()
  const divScrollBar = useRef<HTMLHeadingElement>(null)
  const isPostPage = useMatch(paths.COMMUNITY_POST_PATH)
  const { sendDataHubConsume } = useSendDataHubConsume()
  const [hasScroll, setHasScroll] = useState<boolean>(false)
  const divContainerButtons = useRef<HTMLHeadingElement>(null)
  const [showPicker, setShowPicker] = useState<boolean>(false)
  const [reactions, setReactions] = useState<Reaction[]>(reactionsParam as Reaction[])
  const { shouldCallReactionClick, handlePublicCommunityReactionClick } =
    usePublicCommunityReactionClick()
  const { setComponentSource, setScreenName } = usePublicCommunity()
  const { user } = useUser()

  const heartCode = '0x2764-0xfe0f'
  const screenName = isPostPage ? ScreenNames.COMMUNITY_POST_DETAIL : ScreenNames.COMMUNITY_FEED

  useEffect(() => {
    setReactions(reactionsParam)
  }, [reactionsParam])

  const isActiveScroll = () =>
    setHasScroll(
      Number(divScrollBar.current?.scrollWidth) > Number(divContainerButtons.current?.scrollWidth)
    )

  useEffect(() => {
    const scroll = () => {
      isActiveScroll()
    }

    scroll()
    window.addEventListener('resize', scroll)
    return () => {
      window.removeEventListener('resize', scroll)
    }
  }, [])

  const moreScroll = () => {
    divScrollBar.current!.scrollLeft = Number(divScrollBar.current?.scrollLeft) + 20
  }

  const lessScroll = () => {
    divScrollBar.current!.scrollLeft = Number(divScrollBar.current?.scrollLeft) - 20
  }

  const onReactEvent = (option: string) => {
    if (onReactionSuccess) return

    const getScreenName = () => {
      if (isPostPage) return ScreenNames.COMMUNITY_POST
      return ScreenNames.COMMUNITY_NEW_FEED
    }

    sendDataHubConsume({
      action: Actions.VIEW,
      entity: Entities.COMMUNITY,
      event: {
        eventName: EventNames.TRY_TO_REACT_TO_A_POST,
        screenName: getScreenName(),
        communityId: Number(community.id),
        postId,
        option,
        arrivedFrom: ArrivedFrom.COMMUNITY_NEW_FEED
      }
    })

    optimizely.track(EventNames.TRY_TO_REACT_TO_A_POST)
  }

  const handleSelectEmojiPicker = async (e: EmojiProps) => {
    setShowPicker(false)
    onReaction?.()

    onReactEvent(ReactionsOption.LIKE)

    if (shouldCallReactionClick) {
      setScreenName(screenName)
      setComponentSource(ComponentSources.REACTION_BAR)

      handlePublicCommunityReactionClick()
      return
    }

    const codeReaction = EmojiUtils.convertEmojiCodeSend(e.unified).toLowerCase()
    const reactionExistInArray = reactions?.find((react) => react.reactionCode === codeReaction)
    const reactExistInArrayPosition =
      reactionExistInArray && reactions.indexOf(reactionExistInArray)

    if (reactionExistInArray && reactionExistInArray.count > 0) {
      if (reactionExistInArray?.reactedByUser) {
        setReactions((prevState) => {
          if (reactionExistInArray.count === 1 && reactionExistInArray.reactionCode !== heartCode) {
            return prevState.filter((react) => react.reactionCode !== codeReaction)
          }

          prevState[reactExistInArrayPosition!] = {
            reactionCode: codeReaction,
            count: reactionExistInArray.count - 1,
            reactedByUser: false
          }

          return [...prevState]
        })

        await deleteReaction(postId, codeReaction)
        return
      } else {
        setReactions((prevState) => {
          prevState[reactExistInArrayPosition!] = {
            reactionCode: codeReaction,
            count: reactionExistInArray.count + 1,
            reactedByUser: true
          }
          return [...prevState]
        })
      }
    } else {
      if (codeReaction === heartCode) {
        setReactions((prevState) => {
          prevState[0] = {
            reactionCode: codeReaction,
            count: (reactionExistInArray?.count || 0) + 1,
            reactedByUser: true
          }
          return [...prevState]
        })
      } else {
        setReactions((prevState) => {
          return [
            ...prevState,
            {
              reactionCode: codeReaction,
              count: 1,
              reactedByUser: true
            }
          ]
        })
      }
    }
    try {
      await addReaction(postId, codeReaction)
      onReactionSuccess?.()
    } catch (error) {
      const err = error as { statusCode: number; message: string }

      onReactionError?.({
        statusCode: err.statusCode,
        errorMessage: err.message
      })
    } finally {
      isActiveScroll()
    }
  }

  if (!user?.token) {
    return null
  }

  return (
    <div className={className}>
      <EmojiPicker
        isOpen={showPicker}
        close={() => setShowPicker(false)}
        handleSelectEmoji={handleSelectEmojiPicker}
      />
      <div
        className={clsx('_mt-3 _d-flex _align-items-start', { '_mb-4': hasScroll })}
        data-test="reactions-bar"
      >
        <PillButton
          ariaLabel="add reactions"
          handleClick={() => setShowPicker(!showPicker)}
          testId="open-picker"
        >
          <Icon icon={faFaceSmilePlus} size="lg" />
        </PillButton>

        {hasScroll && (
          <PillButton handleClick={lessScroll} ariaLabel="previous reactions">
            <Icon icon={faArrowLeft} />
          </PillButton>
        )}

        <ContainerButtons ref={divContainerButtons} className="_mr-2 _w-full _position-relative">
          <Content
            ref={divScrollBar}
            className="_d-flex _w-full _pb-2 _mb-1 _mr-1 _position-absolute"
          >
            <ReactionsList
              postId={postId}
              heartCode={heartCode}
              showAnimation={showAnimation}
              reactions={reactions}
              setReactions={setReactions}
              onReactEvent={onReactEvent}
              onReactionSuccess={onReactionSuccess}
              onReactionError={onReactionError}
              onReaction={onReaction}
            />
          </Content>
        </ContainerButtons>

        {hasScroll && (
          <PillButton handleClick={moreScroll} ariaLabel="next reactions">
            <Icon icon={faArrowRight} />
          </PillButton>
        )}
      </div>
    </div>
  )
}

export default ReactionsBar
